import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { LandingPlane } from "../components/LandingPlane";
import { HeroConsoleSearch } from "./HeroConsoleSearch";
import { trackEvent } from "@/lib/analytics";
import { sendAnalyticsEvent } from "@/lib/api/analyticsEvents";
import heroImage from "../assets/hero-instrument.jpg";

/**
 * Landing v2 — Hero (Operations Console)
 *
 * Plane: paper → instrument fade. The instrument plane carries an editorial
 * data-center photograph as a desaturated full-bleed underlay; copy sits on
 * the upper-left third where the original frame is intentionally near-black.
 *
 * No sparkles, no lamp, no aurora. Three earned motions only (defined in v2
 * styles): the thermal sweep on the H1 word "infrastructure", a 1-pixel
 * scanline (only at md+ per the mobile inventory), and tile hover-lift.
 */
export function HeroSection() {
  return (
    <LandingPlane
      variant="full-bleed"
      aria-label="Rack & Role — archived job platform for critical infrastructure, cleared talent, and AI-assisted builders"
      className="relative isolate overflow-hidden"
    >
      {/* Full-bleed editorial photograph */}
      {/* Saturate + brightness are baked into the JPG (saves runtime CSS filter
          compositing on every paint). */}
      <img
        src={heroImage}
        alt=""
        aria-hidden="true"
        width={1920}
        height={1088}
        decoding="async"
        fetchPriority="high"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover object-right"
      />
      {/* Gradient overlay — smooth continuous easing (no hard stops) to avoid
          the Mach-band seam that hard color-hold stops were producing. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(100deg, hsl(var(--surface-instrument)) 0%, hsl(var(--surface-instrument) / 0.94) 25%, hsl(var(--surface-instrument) / 0.72) 50%, hsl(var(--surface-instrument) / 0.38) 75%, hsl(var(--surface-instrument) / 0.15) 100%)",
        }}
      />
      {/* Soft left-edge vignette — guarantees no visible seam between the
          dark copy lane and the underlying photograph at any viewport width. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 -z-10 w-1/2"
        style={{
          background:
            "radial-gradient(120% 100% at 0% 50%, hsl(var(--surface-instrument)) 0%, hsl(var(--surface-instrument) / 0) 75%)",
        }}
      />
      {/* Hairline scanline — disabled on mobile */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 hidden h-px md:block"
        style={{
          background:
            "linear-gradient(to right, transparent, hsl(var(--surface-instrument-fg) / 0.18), transparent)",
          animation: "scanline-sweep 8s linear infinite",
        }}
      />

      <div className="container relative mx-auto px-4 pt-20 pb-16 sm:pt-28 sm:pb-24 lg:pt-32 lg:pb-28">
        <div className="max-w-3xl">
          {/* Status pill — mono, single line. SECTION 01 · HERO. */}
          <div className="mono-meta mb-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--surface-instrument-fg)/0.7)]">
            <span
              aria-hidden="true"
              className="relative inline-flex h-1.5 w-1.5"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--live-led))] opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--live-led))]" />
            </span>
            <span>Archived snapshot · job data frozen 18 Aug 2026</span>
          </div>

          {/* Headline — plain English. No "operations layer" jargon. The brand
              accent ("infrastructure") still carries the thermal sweep so the
              identity stays distinctive without burying meaning. */}
          <h1
            className="text-balance break-words font-display text-[clamp(2rem,8.5vw,3rem)] font-black leading-[1.04] tracking-tight text-[hsl(var(--surface-instrument-fg))] sm:text-5xl md:text-6xl lg:text-7xl"
            style={{ fontFamily: "'Sora', 'Sora-fallback', ui-sans-serif, system-ui, sans-serif" }}
          >
            Rack &amp; Role: the job board built for the people who build and run the{" "}
            <span className="relative inline-block max-w-full">
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: "var(--gradient-thermal)",
                  backgroundSize: "200% 100%",
                  animation: "thermal-sweep 20s linear infinite",
                }}
              >
                infrastructure
              </span>
            </span>
            .
          </h1>

          {/* Subhead — plain English. Hero OWNS: free core access headline. */}
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[hsl(var(--surface-instrument-fg)/0.78)] sm:text-lg">
            It let you search by certification, clearance, shift, and the systems you actually
            work on. The platform stopped operating on 18 August 2026 and is preserved here as a
            read-only snapshot — browse the board exactly as it stood, or read how it was built.
          </p>

          {/* Live search — type a role and go straight to the board.
              No sign-up required to browse. */}
          <div className="mt-10">
            <HeroConsoleSearch />
          </div>

          {/* Dual CTA — candidates browse first, employers go straight to their lane. */}
          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
            <Link
              to="/jobs"
              onClick={() => {
                trackEvent("landing_cta_clicked", {
                  cta_id: "hero_primary_browse_jobs",
                  variant: "candidate",
                  location: "hero",
                });
                void sendAnalyticsEvent({
                  event_type: "landing_cta_clicked",
                  metadata: { cta_id: "hero_primary_browse_jobs", variant: "candidate", location: "hero" },
                }).catch(() => null);
              }}
              className="group inline-flex h-12 items-center justify-center gap-2 rounded-md bg-[hsl(var(--signal-orange))] px-6 text-sm font-semibold text-[hsl(var(--signal-orange-fg))] shadow-[0_20px_60px_-20px_hsl(var(--signal-orange)/0.7)] transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--signal-orange)/0.6)]"
            >
              Browse the frozen board
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
            <Link
              to="/archive/how-it-worked"
              onClick={() => {
                trackEvent("landing_cta_clicked", {
                  cta_id: "hero_secondary_signup",
                  variant: "candidate",
                  location: "hero",
                });
                void sendAnalyticsEvent({
                  event_type: "landing_cta_clicked",
                  metadata: { cta_id: "hero_secondary_signup", variant: "candidate", location: "hero" },
                }).catch(() => null);
              }}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[hsl(var(--surface-instrument-fg)/0.85)] transition-colors hover:text-[hsl(var(--signal-orange))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--signal-orange)/0.5)] rounded"
            >
              How it was built
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
            <Link
              to="/employers"
              onClick={() => {
                trackEvent("landing_cta_clicked", {
                  cta_id: "hero_tertiary_employers",
                  variant: "employer",
                  location: "hero",
                });
              }}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--surface-instrument-fg)/0.55)] underline-offset-4 transition-colors hover:text-[hsl(var(--infra-teal))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--infra-teal)/0.5)] rounded sm:ml-auto"
            >
              See the employer side →
            </Link>
          </div>
        </div>
      </div>
    </LandingPlane>
  );
}
